import { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Card, cn } from '@saas-pages/shared/ui'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { OptionToggle } from '@/shared/ui/OptionToggle'
import { useCopyToClipboard } from '@/shared/ui/useCopyToClipboard'
import {
  dedupeText,
  type DedupeOption,
  type DedupeOptions,
} from '@/pages/udalit-dubli-strok/dedupe-lines'
import { reportDedupeSuccess } from '@/pages/udalit-dubli-strok/dedupe-lines-analytics'

function downloadBlob(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function DedupeLinesPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [opts, setOpts] = useState<DedupeOptions>({
    ignoreCase: false,
    ignoreOuterSpaces: false,
    removeBlankLines: false,
  })
  const reportedInitialOperation = useRef(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const { copied, copy } = useCopyToClipboard()
  const hasInput = input.length > 0

  const result = useMemo(() => dedupeText(input, opts), [input, opts])
  const { output, stats } = result

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const startedAt = performance.now()
      const next = event.target.value
      if (!reportedInitialOperation.current && next.length > 0) {
        reportDedupeSuccess(
          opts,
          dedupeText(next, opts),
          performance.now() - startedAt,
        )
        reportedInitialOperation.current = true
      }
      setInput(next)
    },
    [opts],
  )

  const toggleOption = useCallback((key: DedupeOption) => {
    const startedAt = performance.now()
    const next = { ...opts, [key]: !opts[key] }
    setOpts(next)
    if (input.length > 0) {
      reportDedupeSuccess(
        next,
        dedupeText(input, next),
        performance.now() - startedAt,
      )
    }
  }, [input, opts])

  const handleClear = useCallback(() => {
    setInput('')
    reportedInitialOperation.current = false
    inputRef.current?.focus()
  }, [])

  const handleCopy = useCallback(() => {
    if (output) {
      copy(output)
    }
  }, [copy, output])

  const handleDownload = useCallback(() => {
    if (output) {
      downloadBlob(output, 'unikalnye-stroki.txt')
    }
  }, [output])

  const p = t.tools.dedupeLines.page

  const optionToggles: Array<{
    key: DedupeOption
    label: string
    description: string
  }> = [
    {
      key: 'ignoreCase',
      label: p.ignoreCase,
      description: p.ignoreCaseDesc,
    },
    {
      key: 'ignoreOuterSpaces',
      label: p.ignoreOuterSpaces,
      description: p.ignoreOuterSpacesDesc,
    },
    {
      key: 'removeBlankLines',
      label: p.removeBlankLines,
      description: p.removeBlankLinesDesc,
    },
  ]

  return (
    <ToolPageShell toolKey="dedupeLines">
      <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
        <div className="min-w-0 space-y-3">
          <Card className="overflow-hidden shadow-sm">
            <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
              <div>
                <div className="text-sm font-medium tracking-normal">{p.sourceText}</div>
                <div className="text-xs text-muted-foreground">
                  {stats.totalLines.toLocaleString(localeCode)} {p.linesUnit}
                </div>
              </div>
              {hasInput && (
                <Button variant="outline" size="sm" onClick={handleClear}>
                  {p.clear}
                </Button>
              )}
            </div>

            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              placeholder={p.inputPlaceholder}
              className={cn(
                'block min-h-[180px] w-full resize-y border-0 p-4 sm:p-5 lg:min-h-[280px]',
                'bg-card font-mono text-[15px] leading-7 text-foreground',
                'placeholder:text-muted-foreground/55',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
              )}
              aria-label={p.inputAriaLabel}
            />
          </Card>

          <fieldset className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            <legend className="sr-only">{p.optionsTitle}</legend>
            {optionToggles.map((opt) => (
              <OptionToggle
                key={opt.key}
                label={opt.label}
                description={opt.description}
                checked={opts[opt.key]}
                onChange={() => toggleOption(opt.key)}
              />
            ))}
          </fieldset>
        </div>

        <Card className="flex min-w-0 flex-col overflow-hidden shadow-sm">
          <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
            <div>
              <div className="text-sm font-medium tracking-normal">{p.resultText}</div>
              {hasInput && (
                <div className="text-xs text-muted-foreground">
                  {stats.uniqueLines.toLocaleString(localeCode)} {p.linesUnit},{' '}
                  <span className="text-destructive">
                    −{stats.duplicateLines.toLocaleString(localeCode)} {p.duplicatesUnit}
                  </span>
                </div>
              )}
            </div>
            {output.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  {copied ? p.copied : p.copy}
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  {p.download}
                </Button>
              </div>
            )}
          </div>

          <textarea
            value={output}
            readOnly
            placeholder={p.outputPlaceholder}
            className={cn(
              'block min-h-[180px] w-full flex-1 resize-y border-0 p-4 sm:p-5 lg:min-h-[280px]',
              'bg-card font-mono text-[15px] leading-7 text-foreground',
              'placeholder:text-muted-foreground/55',
              'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
            )}
            aria-label={p.outputAriaLabel}
          />
        </Card>
      </div>

      {/* Stats */}
      {hasInput && (
        <Card className="mt-4 shadow-sm">
          <div className="border-b bg-muted/45 px-4 py-3">
            <div className="text-sm font-medium tracking-normal">{p.statsTitle}</div>
          </div>
          <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
            <StatBlock
              label={p.statTotal}
              value={stats.totalLines.toLocaleString(localeCode)}
            />
            <StatBlock
              label={p.statUnique}
              value={stats.uniqueLines.toLocaleString(localeCode)}
            />
            <StatBlock
              label={p.statRemoved}
              value={stats.duplicateLines.toLocaleString(localeCode)}
              highlight={stats.duplicateLines > 0 ? 'text-destructive' : undefined}
            />
            <StatBlock
              label={p.statBlankRemoved}
              value={stats.blankLinesRemoved.toLocaleString(localeCode)}
            />
          </div>
        </Card>
      )}

      {/* SEO section */}
      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{p.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{p.itemRule} {p.orderRule}</p>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{p.howText}</p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{p.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{p.whenText}</p>
        </div>
      </section>
    </ToolPageShell>
  )
}

function StatBlock({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: string
}) {
  return (
    <div className="text-center">
      <div className={cn('text-2xl font-bold tabular-nums tracking-normal', highlight)}>
        {value}
      </div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
    </div>
  )
}
